import { Check, GraduationCap, Briefcase, MapPin, Target, Search, Compass, Sparkles, Users } from "lucide-react";

export const About = () => {
  const timeline = [
    {
      icon: GraduationCap,
      title: "Formazione",
      text: "Laurea in Economia e Management per l'Impresa · Università Cattolica del Sacro Cuore",
    },
    {
      icon: Briefcase,
      title: "Prime esperienze",
      text: "Avvio dell'attività di locazione breve su Milano e sui laghi — Como, Lecco, Garda",
    },
    {
      icon: Target,
      title: "Abilitazione",
      text: "Abilitazione alla professione di agente immobiliare",
    },
    {
      icon: MapPin,
      title: "Oggi",
      text: "Oltre 5 anni di attività · 20 immobili gestiti tra Milano e i Laghi · clienti su tutto il territorio nazionale",
    },
  ];

  const method = [
    {
      icon: Search,
      title: "Analizzo l'immobile",
      text: "Posizione, caratteristiche, potenziale di mercato. Numeri reali zona per zona.",
    },
    {
      icon: Compass,
      title: "Definiamo la strategia",
      text: "Affitti brevi, transitori, stagionali o vendita: la formula con il miglior rendimento per te.",
    },
    {
      icon: Sparkles,
      title: "Gestione operativa",
      text: "Preparo e adatto l'immobile alla soluzione scelta, in tutte le sue fasi.",
    },
    {
      icon: Users,
      title: "Ricerca clienti a target",
      text: "Inquilini o acquirenti realmente in linea con gli obiettivi del proprietario.",
    },
  ];

  return (
    <section
      id="chi-sono"
      data-testid="about-section"
      className="relative bg-brand-bg py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Portrait */}
          <div className="lg:col-span-5 order-2 lg:order-1 lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute -inset-3 bg-brand-beige/40 -z-10" />
              <img
                src="https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/qb7b5d8h_ChatGPT%20Image%2022%20apr%202026%2C%2015_00_21.png"
                alt="Francesca Gorla, agente immobiliare"
                data-testid="about-portrait"
                className="w-full aspect-[4/5] object-cover rounded-sm shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-white border border-brand-line px-6 py-4 rounded-sm shadow-md hidden sm:block">
                <div className="font-display text-xl text-brand-ink">Francesca Gorla</div>
                <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mt-1">
                  Agente Immobiliare
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="divider-ornament" data-testid="about-eyebrow">Chi sono</p>
            <h2
              data-testid="about-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-bold text-brand-ink mt-8"
            >
              Dal mattone al<br />
              <span className="italic font-normal">margine reale</span>.
            </h2>

            <div className="mt-8 space-y-5 max-w-xl">
              <p className="text-lg leading-relaxed text-brand-muted" data-testid="about-paragraph-1">
                Mi sono laureata in <span className="text-brand-ink font-medium">Economia e Management per l'Impresa</span> all'Università Cattolica del Sacro Cuore di Milano e da oltre <span className="text-brand-ink font-medium">cinque anni</span> gestisco un portafoglio di immobili tra Milano e i Laghi (Como, Lecco, Garda).
              </p>
              <p className="text-lg leading-relaxed text-brand-muted" data-testid="about-paragraph-2">
                Oggi mi occupo di locazione e vendita su tutto il territorio nazionale, ma il cuore del mio lavoro resta a Milano: una città con enormi investimenti immobiliari, che però ha bisogno di una figura che conosca davvero il mercato e la <span className="text-brand-ink font-medium">rendita reale zona per zona</span>.
              </p>
              <p className="text-lg leading-relaxed text-brand-muted" data-testid="about-paragraph-3">
                È proprio questo che mi ha spinto a focalizzarmi sugli affitti. L'immobile resta l'asset più amato dagli italiani, ma bisogna sapere <span className="text-brand-ink font-medium">dove si guadagna davvero</span>.
                In un mercato ormai saturo di affitti brevi, questa formula funziona solo per immobili di nicchia.
                Tutti gli altri devono rispondere alla domanda, in forte crescita, di <span className="text-brand-ink font-medium">locazioni transitorie e tradizionali</span>.
              </p>
              <p className="text-lg leading-relaxed text-brand-muted" data-testid="about-paragraph-4">
                Milano è cara, e chi la sceglie spesso non può permettersi di restarci a lungo. Offrendo una soluzione abitativa fino a <span className="text-brand-ink font-medium">18 mesi</span>, intercettiamo una fetta di mercato con una domanda in crescita — con margini più alti, meno gestione e una fiscalità agevolata.
              </p>
              <p className="text-lg leading-relaxed text-brand-muted" data-testid="about-paragraph-5">
                Non mi definisco <span className="text-brand-ink font-medium">"Agente immobiliare"</span>, ma <span className="text-brand-ink font-medium">"Consulente"</span>: il mio ruolo è guidarti verso la scelta più efficace per valorizzare il tuo patrimonio. Ogni decisione viene presa considerando rendimento, rischi e obiettivi — con una gestione diretta e professionale in tutte le fasi.
              </p>
            </div>

            {/* Method */}
            <div className="mt-14" data-testid="about-method">
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink mb-6">
                Il mio metodo · Semplice e concreto
              </p>
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                {method.map((m, i) => (
                  <div
                    key={m.title}
                    data-testid={`about-method-${i}`}
                    className="bg-white border border-brand-line p-5 rounded-sm flex gap-4"
                  >
                    <div className="h-10 w-10 shrink-0 flex items-center justify-center border border-brand-beigeDark text-brand-ink rounded-sm">
                      <m.icon size={18} strokeWidth={1.7} />
                    </div>
                    <div>
                      <div className="font-display text-base font-semibold text-brand-ink leading-tight">
                        {m.title}
                      </div>
                      <p className="mt-1.5 text-sm text-brand-muted leading-snug">
                        {m.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <ol
              data-testid="about-timeline"
              className="mt-14 border-l border-brand-beigeDark/60 space-y-7 pl-7 max-w-xl"
            >
              {timeline.map((t) => (
                <li key={t.title} className="relative" data-testid={`about-timeline-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="absolute -left-[34px] top-1 h-6 w-6 bg-white border border-brand-beigeDark rounded-sm flex items-center justify-center text-brand-ink">
                    <t.icon size={12} strokeWidth={2} />
                  </span>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink">
                    {t.title}
                  </div>
                  <p className="mt-1.5 text-sm sm:text-base text-brand-muted leading-relaxed">
                    {t.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};
